import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from "@/components/ui/dialog";
import { Plus, Download, Package, AlertTriangle, Pencil, Trash2, UtensilsCrossed, AlertCircle, Calendar, FileText, HelpCircle, RotateCcw, CheckCircle2, Circle, CheckSquare } from "lucide-react";
import jsPDF from "jspdf";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useCateringStock } from "@/hooks/useCateringStock";
import { StockItemRow } from "@/components/catering/StockItemRow";
import { ReturnStockDialog } from "@/components/catering/ReturnStockDialog";
import { StockLogTab } from "@/components/catering/StockLogTab";
import { formatQuantityWithUnit } from "@/lib/unitPluralize";
import { TaskAssigneeSelector } from "@/components/TaskAssigneeSelector";
import { 
  StockItemWithBatches,
  StockBatch,
  CateringMenuItem, 
  Event, 
  EventAssignment, 
  CustomCategory,
  DEFAULT_CATEGORIES,
  UNIT_TYPES,
  allergenTooltips
} from "@/types/catering";

interface Task {
  id: string;
  title: string;
  description: string | null;
  task_type: 'personal' | 'assigned' | 'general';
  assigned_to: string | null;
  status: 'pending' | 'completed';
  created_at: string;
  created_by: string | null;
  role_context?: string | null;
  task_group_id?: string | null;
  assigned_role?: string | null;
}

interface Member {
  id: string;
  full_name: string;
  user_id: string | null;
}

export default function CateringDashboard() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [ptaId, setPtaId] = useState<string | null>(null);
  
  // Use the new stock hook
  const {
    stockItems,
    customSuppliers,
    loading: stockLoading,
    fetchStockItems,
    addCustomSupplier,
    getAllSupplierOptions,
    addStockItem,
    updateStockItem,
    deleteStockItem,
    addBatch,
    updateBatch,
    deleteBatch,
    assignToEvent,
    returnFromEvent
  } = useCateringStock(ptaId);

  // Dialog states
  const [isAddItemDialogOpen, setIsAddItemDialogOpen] = useState(false);
  const [isEditItemDialogOpen, setIsEditItemDialogOpen] = useState(false);
  const [isAddBatchDialogOpen, setIsAddBatchDialogOpen] = useState(false);
  const [isEditBatchDialogOpen, setIsEditBatchDialogOpen] = useState(false);
  const [isAssignStockDialogOpen, setIsAssignStockDialogOpen] = useState(false);
  const [isReturnStockDialogOpen, setIsReturnStockDialogOpen] = useState(false);
  const [isAddMenuItemDialogOpen, setIsAddMenuItemDialogOpen] = useState(false);
  const [isEditMenuItemDialogOpen, setIsEditMenuItemDialogOpen] = useState(false);
  const [isViewEventDialogOpen, setIsViewEventDialogOpen] = useState(false);
  
  // Selected items for editing
  const [selectedItem, setSelectedItem] = useState<StockItemWithBatches | null>(null);
  const [selectedBatch, setSelectedBatch] = useState<StockBatch | null>(null);
  const [selectedMenuItem, setSelectedMenuItem] = useState<CateringMenuItem | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<EventAssignment | null>(null);
  
  // Form states for stock items
  const [itemName, setItemName] = useState("");
  const [selectedSupplier, setSelectedSupplier] = useState("");
  const [customSupplierName, setCustomSupplierName] = useState("");
  const [selectedUnitType, setSelectedUnitType] = useState("units");
  const [lowStockThreshold, setLowStockThreshold] = useState("");
  
  // Form states for batches
  const [batchNumber, setBatchNumber] = useState("");
  const [batchQuantity, setBatchQuantity] = useState("");
  const [batchExpiry, setBatchExpiry] = useState("");
  
  // Assignment state
  const [selectedEventId, setSelectedEventId] = useState("");
  const [assignQuantity, setAssignQuantity] = useState("");

  // Menu item states
  const [selectedMenuItems, setSelectedMenuItems] = useState<string[]>([]);
  const [selectedIcon, setSelectedIcon] = useState<string | null>("🍔");
  const [editSelectedIcon, setEditSelectedIcon] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>("Food");
  const [editSelectedCategory, setEditSelectedCategory] = useState<string>("Food");
  const [customCategoryName, setCustomCategoryName] = useState("");
  const [editCustomCategoryName, setEditCustomCategoryName] = useState("");
  const [customCategories, setCustomCategories] = useState<CustomCategory[]>([]);
  
  // Data from database
  const [menuItems, setMenuItems] = useState<CateringMenuItem[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [eventAssignments, setEventAssignments] = useState<EventAssignment[]>([]);

  // Task states
  const [tasks, setTasks] = useState<Task[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [isAddTaskDialogOpen, setIsAddTaskDialogOpen] = useState(false);
  const [isEditTaskDialogOpen, setIsEditTaskDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newTaskDescription, setNewTaskDescription] = useState("");
  const [taskAssignees, setTaskAssignees] = useState<string[]>([]);
  const [taskRoles, setTaskRoles] = useState<string[]>([]);

  useEffect(() => {
    if (user?.id) {
      fetchPtaId();
    }
  }, [user]);

  useEffect(() => {
    if (ptaId) {
      fetchMenuItems();
      fetchEvents();
      fetchEventAssignments();
      fetchCustomCategories();
      fetchTasks();
      fetchMembers();
    }
  }, [ptaId]);

  const fetchPtaId = async () => {
    const { data: memberData } = await supabase
      .from('members')
      .select('pta_id')
      .eq('user_id', user?.id)
      .maybeSingle();
    
    if (memberData?.pta_id) {
      setPtaId(memberData.pta_id);
    }
  };

  const fetchTasks = async () => {
    if (!ptaId || !user?.id) return;

    // Fetch tasks: 
    // 1. Tasks created by me from this dashboard (role_context = 'catering')
    // 2. Tasks assigned TO me (from any dashboard)
    const { data, error } = await supabase
      .from('tasks')
      .select('*')
      .eq('pta_id', ptaId)
      .or(`and(role_context.eq.catering,created_by.eq.${user.id}),assigned_to.eq.${user.id}`)
      .order('created_at', { ascending: false });

    if (!error && data) setTasks(data as Task[]);
  };

  const fetchMembers = async () => {
    if (!ptaId) return;
    const { data } = await supabase
      .from('members')
      .select('id, full_name, user_id')
      .eq('pta_id', ptaId)
      .eq('status', 'active');

    if (data) setMembers(data);
  };

  const fetchMenuItems = async () => {
    const { data, error } = await supabase
      .from('catering_menu_items')
      .select('*')
      .eq('pta_id', ptaId)
      .order('name');

    if (!error && data) {
      setMenuItems(data);
    }
  };

  const fetchEvents = async () => {
    const { data, error } = await supabase
      .from('events')
      .select('id, name, event_date')
      .eq('pta_id', ptaId)
      .eq('is_archived', false)
      .order('event_date', { ascending: true });

    if (!error && data) {
      setEvents(data);
    }
  };

  const fetchEventAssignments = async () => {
    const { data: assignments, error } = await supabase
      .from('event_catering_assignments')
      .select(`
        id,
        event_id,
        quantity,
        new_stock_item_id,
        menu_item_id,
        events (name, event_date),
        catering_stock_items!event_catering_assignments_new_stock_item_id_fkey (item_name),
        catering_menu_items (name)
      `)
      .eq('pta_id', ptaId);

    if (!error && assignments) {
      const grouped: Record<string, EventAssignment> = {};
      assignments.forEach((a: any) => {
        if (!grouped[a.event_id]) {
          grouped[a.event_id] = {
            id: a.event_id,
            event_id: a.event_id,
            event_name: a.events?.name || 'Unknown Event',
            event_date: a.events?.event_date || '',
            items: []
          };
        }
        const itemName = a.catering_stock_items?.item_name || a.catering_menu_items?.name || 'Unknown';
        grouped[a.event_id].items.push({ 
          id: a.id,
          name: itemName, 
          quantity: a.quantity,
          stock_item_id: a.new_stock_item_id,
          menu_item_id: a.menu_item_id
        });
      });
      setEventAssignments(Object.values(grouped));
    }
  };

  const fetchCustomCategories = async () => {
    const { data, error } = await supabase
      .from('catering_custom_categories')
      .select('id, category_name')
      .eq('pta_id', ptaId)
      .order('category_name');

    if (!error && data) {
      setCustomCategories(data);
    }
  };

  const addCustomCategory = async (name: string): Promise<boolean> => {
    if (!name.trim() || !ptaId) return false;
    
    const { data, error } = await supabase
      .from('catering_custom_categories')
      .insert({ pta_id: ptaId, category_name: name.trim() })
      .select('id, category_name')
      .single();

    if (!error && data) {
      setCustomCategories(prev => [...prev, data].sort((a, b) => a.category_name.localeCompare(b.category_name)));
      return true;
    }
    return false;
  };

  const getAllCategoryOptions = () => {
    const customNames = customCategories.map(c => c.category_name);
    return [...DEFAULT_CATEGORIES, ...customNames.filter(n => !DEFAULT_CATEGORIES.includes(n))];
  };

  // Reset form states
  const resetItemForm = () => {
    setItemName("");
    setSelectedSupplier("");
    setCustomSupplierName("");
    setSelectedUnitType("units");
    setLowStockThreshold("");
  };

  const resetBatchForm = () => {
    setBatchNumber("");
    setBatchQuantity("");
    setBatchExpiry("");
  };

  // Stock item handlers
  const handleAddItem = async () => {
    let supplierValue = selectedSupplier;
    if (selectedSupplier === "custom" && customSupplierName.trim()) {
      await addCustomSupplier(customSupplierName);
      supplierValue = customSupplierName.trim();
    }

    const itemId = await addStockItem({
      item_name: itemName,
      supplier: supplierValue === "none" ? null : supplierValue || null,
      unit_type: selectedUnitType,
      low_stock_threshold: parseInt(lowStockThreshold) || 0
    });

    if (itemId) {
      setIsAddItemDialogOpen(false);
      resetItemForm();
      // Open add batch dialog for the new item
      const newItem = stockItems.find(i => i.id === itemId);
      if (newItem) {
        setSelectedItem(newItem);
        setIsAddBatchDialogOpen(true);
      }
    }
  };

  const handleEditItem = async () => {
    if (!selectedItem) return;

    let supplierValue = selectedSupplier;
    if (selectedSupplier === "custom" && customSupplierName.trim()) {
      await addCustomSupplier(customSupplierName);
      supplierValue = customSupplierName.trim();
    }

    const success = await updateStockItem(selectedItem.id, {
      item_name: itemName,
      supplier: supplierValue === "none" ? null : supplierValue || null,
      unit_type: selectedUnitType,
      low_stock_threshold: parseInt(lowStockThreshold) || 0
    });

    if (success) {
      setIsEditItemDialogOpen(false);
      resetItemForm();
    }
  };

  const handleOpenEditItem = (item: StockItemWithBatches) => {
    setSelectedItem(item);
    setItemName(item.item_name);
    setSelectedSupplier(item.supplier || "");
    setSelectedUnitType(item.unit_type || "units");
    setLowStockThreshold(item.low_stock_threshold?.toString() || "");
    setIsEditItemDialogOpen(true);
  };

  const handleDeleteItem = async (itemId: string) => {
    await deleteStockItem(itemId);
  };

  // Batch handlers
  const handleOpenAddBatch = (item: StockItemWithBatches) => {
    setSelectedItem(item);
    resetBatchForm();
    setIsAddBatchDialogOpen(true);
  };

  const handleAddBatch = async () => {
    if (!selectedItem) return;

    const success = await addBatch(selectedItem.id, selectedItem.item_name, {
      batch_number: batchNumber || null,
      quantity: parseInt(batchQuantity) || 0,
      expiry_date: batchExpiry || null
    });

    if (success) {
      setIsAddBatchDialogOpen(false);
      resetBatchForm();
    }
  };

  const handleOpenEditBatch = (item: StockItemWithBatches, batch: StockBatch) => {
    setSelectedItem(item);
    setSelectedBatch(batch);
    setBatchNumber(batch.batch_number || "");
    setBatchQuantity(batch.quantity.toString());
    setBatchExpiry(batch.expiry_date || "");
    setIsEditBatchDialogOpen(true);
  };

  const handleEditBatch = async () => {
    if (!selectedBatch || !selectedItem) return;

    const success = await updateBatch(selectedBatch.id, selectedItem.item_name, {
      batch_number: batchNumber || null,
      quantity: parseInt(batchQuantity) || 0,
      expiry_date: batchExpiry || null
    });

    if (success) {
      setIsEditBatchDialogOpen(false);
      resetBatchForm();
    }
  };

  const handleDeleteBatch = async (batchId: string, itemName: string, batchNumber?: string | null) => {
    await deleteBatch(batchId, itemName, batchNumber);
  };

  // Assignment handler
  const handleOpenAssignDialog = (item: StockItemWithBatches) => {
    setSelectedItem(item);
    setSelectedEventId("");
    setAssignQuantity("");
    setIsAssignStockDialogOpen(true);
  };

  const handleAssignStock = async () => {
    if (!selectedItem || !selectedEventId || !assignQuantity) {
      toast({ title: "Error", description: "Please select an event and enter quantity", variant: "destructive" });
      return;
    }

    const eventData = events.find(e => e.id === selectedEventId);
    const success = await assignToEvent(selectedItem.id, selectedEventId, parseInt(assignQuantity), eventData?.name);
    if (success) {
      setIsAssignStockDialogOpen(false);
      fetchEventAssignments();
    }
  };

  // Return handler
  const handleOpenReturnDialog = (event: EventAssignment) => {
    setSelectedEvent(event);
    setIsReturnStockDialogOpen(true);
  };

  const handleReturnComplete = () => {
    fetchEventAssignments();
    fetchStockItems();
  };

  // Event handlers
  const handleViewEventDetails = (event: EventAssignment) => {
    setSelectedEvent(event);
    setIsViewEventDialogOpen(true);
  };

  const handleDeleteEventAssignment = async (eventId: string) => {
    const { error } = await supabase.from('event_catering_assignments').delete().eq('event_id', eventId);
    if (error) {
      toast({ title: "Error", description: "Failed to remove event assignments", variant: "destructive" });
    } else {
      toast({ title: "Assignments Removed", description: "Event catering assignments have been removed" });
      fetchEventAssignments();
    }
  };

  // Menu item handlers
  const cateringIcons = [
    { name: "none", icon: null, label: "No Icon" },
    { name: "apple", icon: "🍎", color: "text-red-500" },
    { name: "burger", icon: "🍔", color: "text-orange-500" },
    { name: "pizza", icon: "🍕", color: "text-yellow-500" },
    { name: "hotdog", icon: "🌭", color: "text-amber-500" },
    { name: "fries", icon: "🍟", color: "text-yellow-600" },
    { name: "taco", icon: "🌮", color: "text-orange-600" },
    { name: "sandwich", icon: "🥪", color: "text-yellow-700" },
    { name: "salad", icon: "🥗", color: "text-green-500" },
    { name: "soup", icon: "🍲", color: "text-orange-400" },
    { name: "cake", icon: "🍰", color: "text-pink-500" },
    { name: "cookie", icon: "🍪", color: "text-amber-600" },
    { name: "cupcake", icon: "🧁", color: "text-pink-400" },
    { name: "donut", icon: "🍩", color: "text-pink-600" },
    { name: "icecream", icon: "🍦", color: "text-blue-300" },
    { name: "candy", icon: "🍬", color: "text-pink-500" },
    { name: "lollipop", icon: "🍭", color: "text-purple-500" },
    { name: "chocolate", icon: "🍫", color: "text-amber-800" },
    { name: "coffee", icon: "☕", color: "text-amber-700" },
    { name: "tea", icon: "🫖", color: "text-green-600" },
    { name: "juice", icon: "🧃", color: "text-orange-400" },
    { name: "water", icon: "💧", color: "text-blue-500" },
    { name: "soda", icon: "🥤", color: "text-red-400" },
    { name: "milk", icon: "🥛", color: "text-blue-100" },
  ];

  const handleAddMenuItem = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    
    const allergens: string[] = [];
    if (formData.get('allergen-v')) allergens.push('V');
    if (formData.get('allergen-ve')) allergens.push('VE');
    if (formData.get('allergen-gf')) allergens.push('GF');
    if (formData.get('allergen-df')) allergens.push('DF');

    let categoryValue = selectedCategory;
    if (selectedCategory === "custom" && customCategoryName.trim()) {
      await addCustomCategory(customCategoryName);
      categoryValue = customCategoryName.trim();
    }

    const { error } = await supabase.from('catering_menu_items').insert({
      pta_id: ptaId,
      name: formData.get('menu-item-name') as string,
      price: parseFloat(formData.get('menu-item-price') as string),
      allergens,
      icon: selectedIcon,
      color: 'bg-amber-100',
      category: categoryValue === "none" ? null : categoryValue
    });

    if (error) {
      toast({ title: "Error", description: "Failed to add menu item", variant: "destructive" });
    } else {
      toast({ title: "Menu Item Added", description: "New menu item has been added successfully" });
      setIsAddMenuItemDialogOpen(false);
      setSelectedIcon("🍔");
      setSelectedCategory("Food");
      setCustomCategoryName("");
      fetchMenuItems();
    }
  };

  const handleEditMenuItem = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedMenuItem) return;
    
    const formData = new FormData(e.currentTarget);
    
    const allergens: string[] = [];
    if (formData.get('edit-allergen-v')) allergens.push('V');
    if (formData.get('edit-allergen-ve')) allergens.push('VE');
    if (formData.get('edit-allergen-gf')) allergens.push('GF');
    if (formData.get('edit-allergen-df')) allergens.push('DF');
    
    let categoryValue = editSelectedCategory;
    if (editSelectedCategory === "custom" && editCustomCategoryName.trim()) {
      await addCustomCategory(editCustomCategoryName);
      categoryValue = editCustomCategoryName.trim();
    }
    
    const { error } = await supabase.from('catering_menu_items')
      .update({ 
        name: formData.get('edit-menu-item-name') as string,
        price: parseFloat(formData.get('edit-menu-item-price') as string),
        allergens,
        icon: editSelectedIcon,
        category: categoryValue === "none" ? null : categoryValue
      })
      .eq('id', selectedMenuItem.id);

    if (error) {
      toast({ title: "Error", description: "Failed to update menu item", variant: "destructive" });
    } else {
      toast({ title: "Menu Item Updated", description: "Menu item has been updated successfully" });
      setIsEditMenuItemDialogOpen(false);
      setEditSelectedIcon(null);
      setEditSelectedCategory("Food");
      setEditCustomCategoryName("");
      fetchMenuItems();
    }
  };

  const handleOpenEditMenuItem = (item: CateringMenuItem) => {
    setSelectedMenuItem(item);
    setEditSelectedIcon(item.icon);
    setEditSelectedCategory(item.category || "Food");
    setEditCustomCategoryName("");
    setIsEditMenuItemDialogOpen(true);
  };

  const handleDeleteMenuItem = async (itemId: string) => {
    const { error } = await supabase.from('catering_menu_items').delete().eq('id', itemId);
    if (error) {
      toast({ title: "Error", description: "Failed to delete menu item", variant: "destructive" });
    } else {
      toast({ title: "Menu Item Deleted", description: "Item has been removed from the menu" });
      setSelectedMenuItems(prev => prev.filter(id => id !== itemId));
      fetchMenuItems();
    }
  };

  const toggleMenuItemSelection = (itemId: string) => {
    setSelectedMenuItems(prev => 
      prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]
    );
  };

  // Task handlers
  const handleAddTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id || !ptaId) return;

    if (taskAssignees.length === 0 && taskRoles.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one role or person to assign the task to.",
        variant: "destructive",
      });
      return;
    }

    const tasksToInsert: any[] = [];
    const taskGroupId = crypto.randomUUID();
    
    // If "ME" is selected, create a personal task
    if (taskAssignees.includes('me')) {
      tasksToInsert.push({
        title: newTaskTitle,
        description: newTaskDescription || null,
        task_type: 'personal',
        assigned_to: user.id,
        created_by: user.id,
        status: 'pending',
        pta_id: ptaId,
        role_context: 'catering',
      });
    }
    
    // For individual members, create assigned tasks
    const otherAssignees = taskAssignees.filter(id => id !== 'me');
    otherAssignees.forEach(assigneeId => {
      tasksToInsert.push({
        title: newTaskTitle,
        description: newTaskDescription || null,
        task_type: 'assigned',
        assigned_to: assigneeId,
        created_by: user.id,
        status: 'pending',
        pta_id: ptaId,
        role_context: 'catering',
      });
    });

    // For roles, find all members with those roles and create tasks for them
    if (taskRoles.length > 0) {
      const { data: roleMembers } = await supabase
        .from('user_roles')
        .select('user_id, role');
      
      if (roleMembers) {
        const assignedRolesString = taskRoles.join(',');
        for (const role of taskRoles) {
          const usersWithRole = roleMembers.filter(rm => rm.role === role);
          for (const ur of usersWithRole) {
            const member = members.find(m => m.user_id === ur.user_id);
            if (member && member.user_id && !otherAssignees.includes(member.user_id)) {
              tasksToInsert.push({
                title: newTaskTitle,
                description: newTaskDescription || null,
                task_type: 'assigned',
                assigned_to: member.user_id,
                created_by: user.id,
                status: 'pending',
                pta_id: ptaId,
                role_context: 'catering',
                task_group_id: taskGroupId,
                assigned_role: assignedRolesString,
              });
            }
          }
        }
      }
    }

    const { error } = await supabase.from('tasks').insert(tasksToInsert);
    
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      return;
    }

    toast({
      title: "Task added",
      description: tasksToInsert.length === 1 && taskAssignees.includes('me') 
        ? "Personal task created." 
        : `Task(s) assigned to ${tasksToInsert.length} recipient(s).`,
    });
    
    setNewTaskTitle("");
    setNewTaskDescription("");
    setTaskAssignees([]);
    setTaskRoles([]);
    setIsAddTaskDialogOpen(false);
    fetchTasks();
  };

  const handleToggleTask = async (taskId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
    const { error } = await supabase
      .from('tasks')
      .update({ status: newStatus })
      .eq('id', taskId);

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      fetchTasks();
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    const { error } = await supabase.from('tasks').delete().eq('id', taskId);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Task Deleted", description: "Task has been removed" });
      fetchTasks();
    }
  };

  const handleEditTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTask) return;
    
    const { error } = await supabase
      .from('tasks')
      .update({
        title: newTaskTitle,
        description: newTaskDescription || null,
      })
      .eq('id', editingTask.id);

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Task Updated", description: "Task has been updated successfully" });
      setIsEditTaskDialogOpen(false);
      setEditingTask(null);
      setNewTaskTitle("");
      setNewTaskDescription("");
      fetchTasks();
    }
  };

  const personalTasks = tasks.filter(t => t.role_context === 'catering' || t.assigned_to === user?.id);

  // PDF generation
  const renderEmojiToBase64 = (emoji: string, size: number = 64): string | null => {
    if (!emoji) return null;
    const scale = 2;
    const canvas = document.createElement('canvas');
    canvas.width = size * scale;
    canvas.height = size * scale;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.font = `${size * scale * 0.85}px "Segoe UI Emoji", "Apple Color Emoji", "Noto Color Emoji", sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(emoji, (size * scale) / 2, (size * scale) / 2);
    return canvas.toDataURL('image/png', 1.0);
  };

  const handleDownloadMenu = () => {
    if (selectedMenuItems.length === 0) {
      toast({ title: "No Items Selected", description: "Please select menu items to download", variant: "destructive" });
      return;
    }
    
    const selectedItems = menuItems.filter(item => selectedMenuItems.includes(item.id));
    const doc = new jsPDF();
    
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('Menu', 105, 20, { align: 'center' });
    
    const groupedItems: Record<string, typeof selectedItems> = {};
    selectedItems.forEach(item => {
      const category = item.category || 'Other';
      if (!groupedItems[category]) groupedItems[category] = [];
      groupedItems[category].push(item);
    });
    
    let yPosition = 40;
    const lineHeight = 20;
    const categorySpacing = 10;
    
    const categoryOrder = ['Drinks', 'Food'];
    const sortedCategories = Object.keys(groupedItems).sort((a, b) => {
      const aIndex = categoryOrder.indexOf(a);
      const bIndex = categoryOrder.indexOf(b);
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return a.localeCompare(b);
    });
    
    sortedCategories.forEach((category) => {
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }
      
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(category, 15, yPosition);
      yPosition += 8;
      doc.setDrawColor(200, 200, 200);
      doc.line(15, yPosition, 195, yPosition);
      yPosition += 8;
      
      doc.setFont('helvetica', 'normal');
      groupedItems[category].forEach((item) => {
        if (yPosition > 270) {
          doc.addPage();
          yPosition = 20;
        }
        
        if (item.icon) {
          const iconBase64 = renderEmojiToBase64(item.icon, 48);
          if (iconBase64) doc.addImage(iconBase64, 'PNG', 15, yPosition - 4, 10, 10);
        }
        
        doc.setFontSize(12);
        doc.text(item.name, item.icon ? 28 : 15, yPosition);
        doc.text(`£${item.price.toFixed(2)}`, 180, yPosition, { align: 'right' });
        
        if (item.allergens.length > 0) {
          doc.setFontSize(9);
          doc.setTextColor(100, 100, 100);
          doc.text(`Allergens: ${item.allergens.join(', ')}`, item.icon ? 28 : 15, yPosition + 5);
          doc.setTextColor(0, 0, 0);
          yPosition += lineHeight + 2;
        } else {
          yPosition += lineHeight - 5;
        }
      });
      yPosition += categorySpacing;
    });
    
    doc.save('menu.pdf');
    toast({ title: "Menu Downloaded", description: `PDF created with ${selectedItems.length} items` });
  };

  const handlePrintPricingSheet = (item: CateringMenuItem) => {
    const doc = new jsPDF({ orientation: 'landscape' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const centerX = pageWidth / 2;
    
    doc.setFontSize(48);
    doc.setFont('helvetica', 'bold');
    doc.text(item.name.toUpperCase(), centerX, 35, { align: 'center' });
    
    if (item.icon) {
      const iconBase64 = renderEmojiToBase64(item.icon, 200);
      if (iconBase64) doc.addImage(iconBase64, 'PNG', centerX - 30, 45, 60, 60);
    }
    
    doc.setFontSize(56);
    doc.setFont('helvetica', 'bold');
    const priceY = item.icon ? 125 : 90;
    doc.text(`£${item.price.toFixed(2)}`, centerX, priceY, { align: 'center' });
    
    if (item.allergens && item.allergens.length > 0) {
      doc.setFontSize(24);
      doc.setFont('helvetica', 'bold');
      const allergensLabelY = priceY + 25;
      doc.text('Allergens:', centerX, allergensLabelY, { align: 'center' });
      doc.setFontSize(28);
      doc.text(item.allergens.join('  •  '), centerX, allergensLabelY + 15, { align: 'center' });
    }
    
    doc.save(`${item.name.replace(/\s+/g, '-')}-pricing-sheet.pdf`);
    toast({ title: "Pricing Sheet Generated", description: `PDF created for ${item.name}` });
  };

  const allergenBadgeColor = (allergen: string) => {
    switch (allergen) {
      case "V": return "bg-green-100 text-green-800";
      case "VE": return "bg-emerald-100 text-emerald-800";
      case "GF": return "bg-blue-100 text-blue-800";
      case "DF": return "bg-purple-100 text-purple-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const renderAllergenCheckbox = (id: string, label: string, namePrefix: string = "", defaultChecked: boolean = false) => (
    <TooltipProvider key={id}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center space-x-2 cursor-help">
            <Checkbox id={`${namePrefix}${id}`} name={`${namePrefix}${id}`} defaultChecked={defaultChecked} />
            <Label htmlFor={`${namePrefix}${id}`} className="cursor-pointer">{label}</Label>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>{allergenTooltips[label]}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  const renderSupplierSelect = (
    value: string, 
    onChange: (val: string) => void,
    customValue: string,
    onCustomChange: (val: string) => void,
    id: string
  ) => (
    <div className="space-y-2">
      <Label htmlFor={id}>Supplier</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={id}>
          <SelectValue placeholder="Select supplier (optional)" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">No supplier</SelectItem>
          {getAllSupplierOptions().map(supplier => (
            <SelectItem key={supplier} value={supplier}>{supplier}</SelectItem>
          ))}
          <SelectItem value="custom">+ Add Custom Supplier</SelectItem>
        </SelectContent>
      </Select>
      {value === "custom" && (
        <Input
          placeholder="Enter custom supplier name"
          value={customValue}
          onChange={(e) => onCustomChange(e.target.value)}
        />
      )}
    </div>
  );

  const renderCategorySelect = (
    value: string, 
    onChange: (val: string) => void,
    customValue: string,
    onCustomChange: (val: string) => void,
    id: string
  ) => (
    <div className="space-y-2">
      <Label htmlFor={id}>Category</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={id}>
          <SelectValue placeholder="Select category" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">No category</SelectItem>
          {getAllCategoryOptions().map(category => (
            <SelectItem key={category} value={category}>{category}</SelectItem>
          ))}
          <SelectItem value="custom">+ Add Custom Category</SelectItem>
        </SelectContent>
      </Select>
      {value === "custom" && (
        <Input
          placeholder="Enter custom category name"
          value={customValue}
          onChange={(e) => onCustomChange(e.target.value)}
        />
      )}
    </div>
  );

  // Calculate stats
  const totalStockItems = stockItems.length;
  const totalBatches = stockItems.reduce((sum, item) => sum + item.batches.length, 0);
  const outOfStockItems = stockItems.filter(item => item.totalQuantity === 0).length;
  const lowStockItems = stockItems.filter(item => item.low_stock_threshold > 0 && item.totalQuantity <= item.low_stock_threshold && item.totalQuantity > 0).length;
  const expiringBatches = stockItems.reduce((sum, item) => {
    const weekFromNow = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
    return sum + item.batches.filter(b => b.expiry_date && new Date(b.expiry_date) <= weekFromNow && b.quantity > 0).length;
  }, 0);

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-4xl font-bold text-foreground">Catering Dashboard</h1>
        <p className="text-muted-foreground mt-2">Manage catering stock, menu items, and event assignments</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Stock Items</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalStockItems}</div>
            <p className="text-sm text-muted-foreground">{totalBatches} total batches</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Menu Items</CardTitle>
            <UtensilsCrossed className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{menuItems.length}</div>
            <p className="text-sm text-muted-foreground">Items on menu</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Stock Alerts</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-500">{outOfStockItems + lowStockItems}</div>
            <p className="text-sm text-muted-foreground">{outOfStockItems} out, {lowStockItems} low</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Expiring Soon</CardTitle>
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-500">{expiringBatches}</div>
            <p className="text-sm text-muted-foreground">Batches expiring this week</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="stock" className="w-full">
        <TabsList>
          <TabsTrigger value="stock">Stock Management</TabsTrigger>
          <TabsTrigger value="menu">Menu & Pricing</TabsTrigger>
          <TabsTrigger value="events">Event Assignments</TabsTrigger>
          <TabsTrigger value="tasks">Tasks</TabsTrigger>
          <TabsTrigger value="log">Activity Log</TabsTrigger>
        </TabsList>

        <TabsContent value="stock" className="space-y-4">
          <Card className="shadow-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Stock Items & Batches</CardTitle>
                  <CardDescription>Manage stock items and their batches. Click an item to view/manage batches.</CardDescription>
                </div>
                <Dialog open={isAddItemDialogOpen} onOpenChange={(open) => {
                  setIsAddItemDialogOpen(open);
                  if (!open) resetItemForm();
                }}>
                  <DialogTrigger asChild>
                    <Button><Plus className="h-4 w-4 mr-2" />Add Stock Item</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add New Stock Item</DialogTitle>
                      <DialogDescription>Create a new stock item, then add batches to it.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="item-name">Item Name</Label>
                        <Input 
                          id="item-name" 
                          value={itemName}
                          onChange={(e) => setItemName(e.target.value)}
                          placeholder="e.g., Crisps, Sweets, Juice"
                          required 
                        />
                      </div>
                      {renderSupplierSelect(
                        selectedSupplier,
                        setSelectedSupplier,
                        customSupplierName,
                        setCustomSupplierName,
                        "supplier"
                      )}
                      <div className="space-y-2">
                        <Label htmlFor="unit-type">Unit Type</Label>
                        <Select value={selectedUnitType} onValueChange={setSelectedUnitType}>
                          <SelectTrigger id="unit-type">
                            <SelectValue placeholder="Select unit type" />
                          </SelectTrigger>
                          <SelectContent>
                            {UNIT_TYPES.map(unit => (
                              <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Label htmlFor="low-stock-threshold">Low Stock Threshold</Label>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                <p>When total stock falls below this, a warning will be shown.</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <Input 
                          id="low-stock-threshold" 
                          type="number" 
                          value={lowStockThreshold}
                          onChange={(e) => setLowStockThreshold(e.target.value)}
                          placeholder="e.g., 10"
                        />
                      </div>
                      <div className="flex justify-end gap-2">
                        <DialogClose asChild><Button type="button" variant="outline">Cancel</Button></DialogClose>
                        <Button onClick={handleAddItem} disabled={!itemName.trim()}>Create Item</Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {stockLoading ? (
                <div className="py-8 text-center text-muted-foreground">Loading stock items...</div>
              ) : stockItems.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground">
                  No stock items yet. Click "Add Stock Item" to get started.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead>Supplier</TableHead>
                      <TableHead>Total Quantity</TableHead>
                      <TableHead>Batches</TableHead>
                      <TableHead className="text-left">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stockItems.map((item) => (
                      <StockItemRow
                        key={item.id}
                        item={item}
                        onEditItem={handleOpenEditItem}
                        onDeleteItem={handleDeleteItem}
                        onAddBatch={handleOpenAddBatch}
                        onEditBatch={handleOpenEditBatch}
                        onDeleteBatch={handleDeleteBatch}
                        onAssignToEvent={handleOpenAssignDialog}
                      />
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="menu" className="space-y-4">
          <Card className="shadow-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Menu Items</CardTitle>
                  <CardDescription>Manage menu items and pricing</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={handleDownloadMenu} disabled={selectedMenuItems.length === 0}>
                    <Download className="h-4 w-4 mr-2" />Download Menu
                  </Button>
                  <Dialog open={isAddMenuItemDialogOpen} onOpenChange={(open) => {
                    setIsAddMenuItemDialogOpen(open);
                    if (!open) {
                      setSelectedIcon("🍔");
                      setSelectedCategory("Food");
                      setCustomCategoryName("");
                    }
                  }}>
                    <DialogTrigger asChild>
                      <Button><Plus className="h-4 w-4 mr-2" />Add Item</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle>Add Menu Item</DialogTitle>
                        <DialogDescription>Add a new item to the menu</DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleAddMenuItem} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="menu-item-name">Item Name</Label>
                          <Input id="menu-item-name" name="menu-item-name" required />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="menu-item-price">Price</Label>
                          <Input id="menu-item-price" name="menu-item-price" type="number" step="0.01" required />
                        </div>
                        {renderCategorySelect(selectedCategory, setSelectedCategory, customCategoryName, setCustomCategoryName, "menu-item-category")}
                        <div className="space-y-2">
                          <Label>Allergens</Label>
                          <div className="flex gap-4">
                            {renderAllergenCheckbox("allergen-v", "V")}
                            {renderAllergenCheckbox("allergen-ve", "VE")}
                            {renderAllergenCheckbox("allergen-gf", "GF")}
                            {renderAllergenCheckbox("allergen-df", "DF")}
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Icon (Optional)</Label>
                          <div className="grid grid-cols-8 gap-2 p-2 border rounded-md">
                            {cateringIcons.map(iconItem => (
                              <Button 
                                key={iconItem.name} 
                                variant={selectedIcon === iconItem.icon ? "default" : "ghost"}
                                size="icon" 
                                type="button"
                                onClick={() => setSelectedIcon(iconItem.icon)}
                                className={selectedIcon === iconItem.icon ? "ring-2 ring-primary" : ""}
                              >
                                {iconItem.icon === null ? <span className="text-xs text-muted-foreground">None</span> : <span className="text-2xl">{iconItem.icon}</span>}
                              </Button>
                            ))}
                          </div>
                        </div>
                        <div className="flex justify-end gap-2">
                          <DialogClose asChild><Button type="button" variant="outline">Cancel</Button></DialogClose>
                          <Button type="submit">Add Item</Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12"></TableHead>
                    <TableHead>Item</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Allergens</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {menuItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Checkbox checked={selectedMenuItems.includes(item.id)} onCheckedChange={() => toggleMenuItemSelection(item.id)} />
                      </TableCell>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {item.icon && <span className="text-lg">{item.icon}</span>}
                          {item.name}
                        </div>
                      </TableCell>
                      <TableCell>{item.category || 'Food'}</TableCell>
                      <TableCell>£{item.price.toFixed(2)}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <TooltipProvider>
                            {item.allergens.map(allergen => (
                              <Tooltip key={allergen}>
                                <TooltipTrigger asChild>
                                  <Badge className={`${allergenBadgeColor(allergen)} cursor-help`}>{allergen}</Badge>
                                </TooltipTrigger>
                                <TooltipContent><p>{allergenTooltips[allergen]}</p></TooltipContent>
                              </Tooltip>
                            ))}
                          </TooltipProvider>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button variant="ghost" size="sm" onClick={() => handleOpenEditMenuItem(item)}><Pencil className="h-4 w-4" /></Button>
                              </TooltipTrigger>
                              <TooltipContent><p>Edit item</p></TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button variant="ghost" size="sm" onClick={() => handlePrintPricingSheet(item)}><FileText className="h-4 w-4" /></Button>
                              </TooltipTrigger>
                              <TooltipContent><p>Generate pricing sheet</p></TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button variant="ghost" size="sm" onClick={() => handleDeleteMenuItem(item.id)}><Trash2 className="h-4 w-4 text-foreground" /></Button>
                              </TooltipTrigger>
                              <TooltipContent><p>Delete item</p></TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="events" className="space-y-4">
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle>Event Assignments</CardTitle>
              <CardDescription>View and manage stock assigned to events</CardDescription>
            </CardHeader>
            <CardContent>
              {eventAssignments.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground">
                  No stock assigned to events yet. Assign stock from the Stock Management tab.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Event</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Items Assigned</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {eventAssignments.map((event) => (
                      <TableRow key={event.id}>
                        <TableCell className="font-medium">{event.event_name}</TableCell>
                        <TableCell>{new Date(event.event_date).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {event.items.slice(0, 3).map((item, idx) => (
                              <Badge key={idx} variant="secondary">{item.name}: {item.quantity}</Badge>
                            ))}
                            {event.items.length > 3 && (
                              <Badge variant="outline">+{event.items.length - 3} more</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button variant="ghost" size="sm" onClick={() => handleViewEventDetails(event)}>
                                    <Calendar className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent><p>View details</p></TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button variant="ghost" size="sm" onClick={() => handleOpenReturnDialog(event)}>
                                    <RotateCcw className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent><p>Return stock</p></TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button variant="ghost" size="sm" onClick={() => handleDeleteEventAssignment(event.event_id)}>
                                    <Trash2 className="h-4 w-4 text-foreground" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent><p>Remove all assignments</p></TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tasks Tab */}
        <TabsContent value="tasks" className="space-y-4">
          <Card className="shadow-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <CheckSquare className="h-5 w-5" />
                  Tasks
                </CardTitle>
                <CardDescription>Manage personal and assigned tasks</CardDescription>
              </div>
              <Dialog open={isAddTaskDialogOpen} onOpenChange={setIsAddTaskDialogOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Task
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add New Task</DialogTitle>
                    <DialogDescription>Create a new task and assign it to members</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleAddTask} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="new-task-title">Task Title</Label>
                      <Input
                        id="new-task-title"
                        placeholder="Task title..."
                        value={newTaskTitle}
                        onChange={(e) => setNewTaskTitle(e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="new-task-description">Description (Optional)</Label>
                      <Textarea
                        id="new-task-description"
                        placeholder="Description..."
                        value={newTaskDescription}
                        onChange={(e) => setNewTaskDescription(e.target.value)}
                        rows={3}
                      />
                    </div>
                    <TaskAssigneeSelector
                      ptaId={ptaId}
                      members={members}
                      selectedAssignees={taskAssignees}
                      selectedRoles={taskRoles}
                      onAssigneesChange={setTaskAssignees}
                      onRolesChange={setTaskRoles}
                      includeMe={true}
                    />
                    <div className="flex justify-end">
                      <Button type="submit">Add Task</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* My Tasks */}
              <div>
                <h3 className="font-medium mb-2">My Tasks</h3>
                <div className="space-y-2">
                  {personalTasks.filter(t => t.task_type === 'personal' || (t.task_type === 'assigned' && t.assigned_to === user?.id && t.created_by !== user?.id)).length === 0 ? (
                    <p className="text-sm text-muted-foreground">No tasks</p>
                  ) : (
                    personalTasks.filter(t => t.task_type === 'personal' || (t.task_type === 'assigned' && t.assigned_to === user?.id && t.created_by !== user?.id)).map((task) => (
                      <div key={task.id} className="flex items-center gap-2 p-3 border rounded-lg">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleToggleTask(task.id, task.status)}
                              >
                                {task.status === 'completed' ? (
                                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                                ) : (
                                  <Circle className="h-5 w-5" />
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{task.status === 'completed' ? 'Mark as incomplete' : 'Mark as complete'}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className={`font-medium ${task.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                              {task.title}
                            </p>
                            {task.created_by !== user?.id && task.role_context && (
                              <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                From: {task.role_context.charAt(0).toUpperCase() + task.role_context.slice(1)}
                              </span>
                            )}
                          </div>
                          {task.description && (
                            <p className="text-sm text-muted-foreground">{task.description}</p>
                          )}
                        </div>
                        {task.created_by === user?.id && (
                          <>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0"
                                    onClick={() => {
                                      setEditingTask(task);
                                      setNewTaskTitle(task.title);
                                      setNewTaskDescription(task.description || '');
                                      setIsEditTaskDialogOpen(true);
                                    }}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Edit task</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0"
                                    onClick={() => handleDeleteTask(task.id)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Delete task</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Assigned Tasks */}
              <div>
                <h3 className="font-medium mb-2">Tasks I've Assigned</h3>
                <div className="space-y-2">
                  {personalTasks.filter(t => t.task_type === 'assigned' && t.created_by === user?.id).length === 0 ? (
                    <p className="text-sm text-muted-foreground">No assigned tasks</p>
                  ) : (
                    personalTasks.filter(t => t.task_type === 'assigned' && t.created_by === user?.id).map((task) => (
                      <div key={task.id} className="flex items-center gap-2 p-3 border rounded-lg">
                        <div className={`w-3 h-3 rounded-full ${task.status === 'completed' ? 'bg-green-500' : 'bg-orange-500'}`} />
                        <div className="flex-1">
                          <p className={`font-medium ${task.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                            {task.title}
                          </p>
                          {task.description && (
                            <p className="text-sm text-muted-foreground">{task.description}</p>
                          )}
                          {task.assigned_role && (
                            <span className="text-xs text-muted-foreground">
                              Assigned to: {task.assigned_role}
                            </span>
                          )}
                        </div>
                        <Badge variant={task.status === 'completed' ? 'default' : 'secondary'}>
                          {task.status === 'completed' ? 'Done' : 'Pending'}
                        </Badge>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => {
                                  setEditingTask(task);
                                  setNewTaskTitle(task.title);
                                  setNewTaskDescription(task.description || '');
                                  setIsEditTaskDialogOpen(true);
                                }}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Edit task</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => handleDeleteTask(task.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Delete task</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Activity Log Tab */}
        <TabsContent value="log" className="space-y-4">
          <StockLogTab ptaId={ptaId} />
        </TabsContent>
      </Tabs>

      {/* Edit Stock Item Dialog */}
      <Dialog open={isEditItemDialogOpen} onOpenChange={(open) => {
        setIsEditItemDialogOpen(open);
        if (!open) resetItemForm();
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Stock Item</DialogTitle>
            <DialogDescription>Update the stock item details.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-item-name">Item Name</Label>
              <Input id="edit-item-name" value={itemName} onChange={(e) => setItemName(e.target.value)} required />
            </div>
            {renderSupplierSelect(selectedSupplier, setSelectedSupplier, customSupplierName, setCustomSupplierName, "edit-supplier")}
            <div className="space-y-2">
              <Label htmlFor="edit-unit-type">Unit Type</Label>
              <Select value={selectedUnitType} onValueChange={setSelectedUnitType}>
                <SelectTrigger id="edit-unit-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {UNIT_TYPES.map(unit => (<SelectItem key={unit} value={unit}>{unit}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-low-stock-threshold">Low Stock Threshold</Label>
              <Input id="edit-low-stock-threshold" type="number" value={lowStockThreshold} onChange={(e) => setLowStockThreshold(e.target.value)} />
            </div>
            <div className="flex justify-end gap-2">
              <DialogClose asChild><Button type="button" variant="outline">Cancel</Button></DialogClose>
              <Button onClick={handleEditItem}>Save Changes</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Batch Dialog */}
      <Dialog open={isAddBatchDialogOpen} onOpenChange={(open) => {
        setIsAddBatchDialogOpen(open);
        if (!open) resetBatchForm();
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Batch to {selectedItem?.item_name}</DialogTitle>
            <DialogDescription>Add a new batch with quantity and expiry date.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label htmlFor="batch-number">Batch Number</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild><HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" /></TooltipTrigger>
                    <TooltipContent className="max-w-xs"><p>Use supplier batch code or your own system (e.g., 0001).</p></TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <Input id="batch-number" value={batchNumber} onChange={(e) => setBatchNumber(e.target.value)} placeholder="e.g., 0001" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="batch-quantity">Quantity ({selectedItem?.unit_type || 'units'})</Label>
              <Input id="batch-quantity" type="number" value={batchQuantity} onChange={(e) => setBatchQuantity(e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="batch-expiry">Expiry Date</Label>
              <Input id="batch-expiry" type="date" value={batchExpiry} onChange={(e) => setBatchExpiry(e.target.value)} />
            </div>
            <div className="flex justify-end gap-2">
              <DialogClose asChild><Button type="button" variant="outline">Cancel</Button></DialogClose>
              <Button onClick={handleAddBatch} disabled={!batchQuantity}>Add Batch</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Batch Dialog */}
      <Dialog open={isEditBatchDialogOpen} onOpenChange={(open) => {
        setIsEditBatchDialogOpen(open);
        if (!open) resetBatchForm();
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Batch</DialogTitle>
            <DialogDescription>Update batch details for {selectedItem?.item_name}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-batch-number">Batch Number</Label>
              <Input id="edit-batch-number" value={batchNumber} onChange={(e) => setBatchNumber(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-batch-quantity">Quantity ({selectedItem?.unit_type || 'units'})</Label>
              <Input id="edit-batch-quantity" type="number" value={batchQuantity} onChange={(e) => setBatchQuantity(e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-batch-expiry">Expiry Date</Label>
              <Input id="edit-batch-expiry" type="date" value={batchExpiry} onChange={(e) => setBatchExpiry(e.target.value)} />
            </div>
            <div className="flex justify-end gap-2">
              <DialogClose asChild><Button type="button" variant="outline">Cancel</Button></DialogClose>
              <Button onClick={handleEditBatch}>Save Changes</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Stock Dialog */}
      <Dialog open={isAssignStockDialogOpen} onOpenChange={(open) => {
        setIsAssignStockDialogOpen(open);
        if (!open) {
          setSelectedEventId("");
          setAssignQuantity("");
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign {selectedItem?.item_name} to Event</DialogTitle>
            <DialogDescription>
              Available: {selectedItem?.totalQuantity} {selectedItem?.unit_type || 'units'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="assign-event">Select Event</Label>
              <Select value={selectedEventId} onValueChange={setSelectedEventId}>
                <SelectTrigger id="assign-event"><SelectValue placeholder="Select an event" /></SelectTrigger>
                <SelectContent>
                  {events.map(event => (
                    <SelectItem key={event.id} value={event.id}>
                      {event.name} ({new Date(event.event_date).toLocaleDateString()})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="assign-quantity">Quantity ({selectedItem?.unit_type || 'units'})</Label>
              <Input 
                id="assign-quantity" 
                type="number" 
                value={assignQuantity} 
                onChange={(e) => setAssignQuantity(e.target.value)}
                max={selectedItem?.totalQuantity}
                min="1"
              />
              {selectedItem && parseInt(assignQuantity) > selectedItem.totalQuantity && (
                <p className="text-sm text-destructive">Cannot assign more than available stock</p>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <DialogClose asChild><Button type="button" variant="outline">Cancel</Button></DialogClose>
              <Button 
                onClick={handleAssignStock} 
                disabled={!selectedEventId || !assignQuantity || parseInt(assignQuantity) > (selectedItem?.totalQuantity || 0)}
              >
                Assign Stock
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Event Details Dialog */}
      <Dialog open={isViewEventDialogOpen} onOpenChange={setIsViewEventDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedEvent?.event_name}</DialogTitle>
            <DialogDescription>
              {selectedEvent?.event_date && new Date(selectedEvent.event_date).toLocaleDateString()}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <h4 className="font-medium">Assigned Items:</h4>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead className="text-right">Quantity</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {selectedEvent?.items.map((item, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{item.name}</TableCell>
                    <TableCell className="text-right">{item.quantity}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="flex justify-end">
              <DialogClose asChild><Button variant="outline">Close</Button></DialogClose>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Menu Item Dialog */}
      <Dialog open={isEditMenuItemDialogOpen} onOpenChange={(open) => {
        setIsEditMenuItemDialogOpen(open);
        if (!open) {
          setEditSelectedIcon(null);
          setEditSelectedCategory("Food");
          setEditCustomCategoryName("");
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Menu Item</DialogTitle>
            <DialogDescription>Update the menu item details</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditMenuItem} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-menu-item-name">Item Name</Label>
              <Input id="edit-menu-item-name" name="edit-menu-item-name" defaultValue={selectedMenuItem?.name} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-menu-item-price">Price</Label>
              <Input id="edit-menu-item-price" name="edit-menu-item-price" type="number" step="0.01" defaultValue={selectedMenuItem?.price} required />
            </div>
            {renderCategorySelect(editSelectedCategory, setEditSelectedCategory, editCustomCategoryName, setEditCustomCategoryName, "edit-menu-item-category")}
            <div className="space-y-2">
              <Label>Allergens</Label>
              <div className="flex gap-4">
                {renderAllergenCheckbox("edit-allergen-v", "V", "", selectedMenuItem?.allergens.includes('V'))}
                {renderAllergenCheckbox("edit-allergen-ve", "VE", "", selectedMenuItem?.allergens.includes('VE'))}
                {renderAllergenCheckbox("edit-allergen-gf", "GF", "", selectedMenuItem?.allergens.includes('GF'))}
                {renderAllergenCheckbox("edit-allergen-df", "DF", "", selectedMenuItem?.allergens.includes('DF'))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Icon</Label>
              <div className="grid grid-cols-8 gap-2 p-2 border rounded-md">
                {cateringIcons.map(iconItem => (
                  <Button 
                    key={iconItem.name} 
                    variant={editSelectedIcon === iconItem.icon ? "default" : "ghost"}
                    size="icon" 
                    type="button"
                    onClick={() => setEditSelectedIcon(iconItem.icon)}
                    className={editSelectedIcon === iconItem.icon ? "ring-2 ring-primary" : ""}
                  >
                    {iconItem.icon === null ? <span className="text-xs text-muted-foreground">None</span> : <span className="text-2xl">{iconItem.icon}</span>}
                  </Button>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <DialogClose asChild><Button type="button" variant="outline">Cancel</Button></DialogClose>
              <Button type="submit">Save Changes</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Return Stock Dialog */}
      <ReturnStockDialog
        open={isReturnStockDialogOpen}
        onOpenChange={setIsReturnStockDialogOpen}
        event={selectedEvent}
        ptaId={ptaId}
        onReturnComplete={handleReturnComplete}
        onReturnStock={returnFromEvent}
      />

      {/* Edit Task Dialog */}
      <Dialog open={isEditTaskDialogOpen} onOpenChange={(open) => {
        setIsEditTaskDialogOpen(open);
        if (!open) {
          setEditingTask(null);
          setNewTaskTitle("");
          setNewTaskDescription("");
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Task</DialogTitle>
            <DialogDescription>Update task details</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditTask} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-task-title">Task Title</Label>
              <Input
                id="edit-task-title"
                value={newTaskTitle}
                onChange={(e) => setNewTaskTitle(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-task-description">Description</Label>
              <Textarea
                id="edit-task-description"
                value={newTaskDescription}
                onChange={(e) => setNewTaskDescription(e.target.value)}
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-2">
              <DialogClose asChild><Button type="button" variant="outline">Cancel</Button></DialogClose>
              <Button type="submit">Save Changes</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
